<template>
  <v-sheet outlined rounded class="pa-4" :class="{ 'tep-exempt': isExempt }">
    <div class="d-flex align-center flex-wrap" style="gap: 8px;">
      <v-icon :color="isExempt ? 'success' : 'primary'" class="mr-1">mdi-shield-check-outline</v-icon>
      <span class="text-subtitle-1">{{ $t('dueDiligenceExemption') }}</span>
      <v-chip small label :color="statusChip.color" :text-color="statusChip.textColor">
        <v-icon small left>{{ statusChip.icon }}</v-icon>{{ statusChip.label }}
      </v-chip>
      <v-spacer/>
      <v-btn v-if="!readOnly && !editing" small outlined color="primary" :loading="loadingList" @click="enterEdit">
        <v-icon small left>mdi-pencil</v-icon>{{ exemption ? $t('change') : $t('selectExemption') }}
      </v-btn>
    </div>

    <template v-if="!editing">
      <template v-if="exemption">
        <p class="text-subtitle-2 mb-0 mt-3">{{ exemption.name }}</p>
        <p v-if="exemption.description" class="text-caption grey--text text--darken-1 mb-0 mt-1">{{ exemption.description }}</p>
        <p v-if="requiresApprovalNote" class="text-caption grey--text text--darken-1 mb-0 mt-3">
          <v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ requiresApprovalNote }}
        </p>
      </template>
      <p v-else class="text-caption grey--text text--darken-1 mb-0 mt-2">{{ $t('notExemptBody') }}</p>
    </template>

    <template v-else>
      <p class="text-caption grey--text text--darken-1 mb-2 mt-3">{{ $t('selectDueDiligenceExemptionHelp') }}</p>

      <v-sheet v-if="allDueDiligenceExemptions.length === 0" outlined rounded class="pa-3 text-center">
        <p class="text-caption grey--text text--darken-1 mb-0">{{ $t('noDueDiligenceExemptionsConfigured') }}</p>
      </v-sheet>

      <template v-else>
        <v-select
        v-model="selectedId"
        :items="allDueDiligenceExemptions"
        item-value="dueDiligenceExemptionId"
        item-text="name"
        :placeholder="$t('selectPlaceholder')"
        :menu-props="{ offsetY: true, maxWidth: menuWidth, maxHeight: '20em' }"
        outlined
        dense
        clearable
        hide-details
        @focus="measureMenuWidth">
          <template #selection="{ item: exemptionOption }">
            <span class="d-flex align-center flex-wrap" style="gap: 6px;">
              {{ exemptionOption.name }}
              <v-chip
              v-for="chip in optionChips(exemptionOption)"
              :key="chip.key"
              x-small
              outlined
              :color="chip.color">
                <v-icon x-small left>{{ chip.icon }}</v-icon>{{ chip.label }}
              </v-chip>
            </span>
          </template>
          <template #item="{ item: exemptionOption }">
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center flex-wrap" style="white-space: normal; gap: 6px;">
                {{ exemptionOption.name }}
                <v-chip
                v-for="chip in optionChips(exemptionOption)"
                :key="chip.key"
                x-small
                outlined
                :color="chip.color">
                  <v-icon x-small left>{{ chip.icon }}</v-icon>{{ chip.label }}
                </v-chip>
              </v-list-item-title>
              <div
              v-if="expandedDescriptionId === exemptionOption.dueDiligenceExemptionId"
              class="mt-1"
              style="white-space: normal;">
                <div class="dd-eyebrow">{{ $t('exemptionDescription') }}</div>
                <div class="text-caption grey--text text--darken-2">{{ exemptionOption.description || $t('noExemptionDescription') }}</div>
              </div>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <v-btn icon small @click.stop="toggleDescription(exemptionOption.dueDiligenceExemptionId)">
                <v-icon color="info" small>{{ expandedDescriptionId === exemptionOption.dueDiligenceExemptionId ? 'mdi-information' : 'mdi-information-outline' }}</v-icon>
              </v-btn>
            </v-list-item-action>
          </template>
        </v-select>

        <p v-if="selectedRequiresApproval" class="text-caption grey--text text--darken-1 mb-0 mt-2">
          <v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t('exemptionRequiresApprovalInfo') }}
        </p>
      </template>

      <div class="d-flex justify-end mt-4" style="gap: 8px;">
        <v-btn small text outlined color="black" @click="cancelEdit">{{ $t('cancel') }}</v-btn>
        <v-btn small color="primary" :disabled="!canSave" :loading="saving" @click="save">
          <v-icon small left>mdi-content-save</v-icon>{{ $t('saveChanges') }}
        </v-btn>
      </div>
    </template>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { menuWidthFromEvent } from '@/utils/menuWidth.js'
import { exemptionOptionChips } from '@/utils/dueDiligence/presentation.js'
import { isTractExempt } from '@/utils/dueDiligence/exemption.js'
import { dueDiligenceApprovalEnabled } from '@/utils/dueDiligence/mode.js'
import { DueDiligenceApprovalStatus } from '@/utils/Enumerations.js'

export default {
  name: 'TractExemptionPanel',

  props: {
    exemption: {
      type: Object,
      required: false,
      default: null
    },

    latestApproval: {
      type: Object,
      required: false,
      default: null
    },

    readOnly: {
      type: Boolean,
      required: false,
      default: false
    },

    saving: {
      type: Boolean,
      required: false,
      default: false
    },

    // Render the exemption as the review cycle left it rather than against the company's current
    // approval setting — set by the reviewer's dialog, which also serves decided history
    historicalView: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    editing: false,
    loadingList: false,
    selectedId: null,
    expandedDescriptionId: null,
    menuWidth: undefined
  }),

  watch: {
    exemption () {
      this.cancelEdit()
    },

    readOnly (value) {
      if (value) this.cancelEdit()
    }
  },

  computed: {
    ...mapGetters('due-diligence-exemption', ['allDueDiligenceExemptions']),

    approvalRequirementActive () {
      return this.historicalView || dueDiligenceApprovalEnabled()
    },

    isExempt () {
      return isTractExempt(this.exemption, this.latestApproval, this.approvalRequirementActive)
    },

    selectedExemption () {
      return this.allDueDiligenceExemptions.find(e => e.dueDiligenceExemptionId === this.selectedId) ?? null
    },

    // Drives the picker hint, so it tracks the live setting even in a historical view — there is no
    // point promising a sign-off that can no longer happen
    selectedRequiresApproval () {
      return !!this.selectedExemption?.requiresApproval && dueDiligenceApprovalEnabled()
    },

    statusChip () {
      if (this.isExempt) return { color: 'success', textColor: 'white', icon: 'mdi-check-decagram', label: this.$t('exempt') }
      if (this.exemption) return { color: 'grey lighten-3', icon: 'mdi-clipboard-check-outline', label: this.$t('exemptionNeedsApproval') }
      return { color: 'grey lighten-3', icon: 'mdi-minus-circle-outline', label: this.$t('notExempt') }
    },

    requiresApprovalNote () {
      if (this.isExempt || !this.exemption?.requiresApproval) return null
      return this.latestApproval?.status === DueDiligenceApprovalStatus.PendingReview.value
        ? this.$t('exemptionAwaitingApprovalInfo')
        : this.$t('exemptionRequiresApprovalInfo')
    },

    dirty () {
      return this.selectedId !== (this.exemption?.dueDiligenceExemptionId ?? null)
    },

    canSave () {
      return this.dirty
    }
  },

  methods: {
    ...mapActions('due-diligence-exemption', ['fetchDueDiligenceExemptions']),

    optionChips (exemption) {
      return exemptionOptionChips(exemption, this.approvalRequirementActive)
    },

    measureMenuWidth (event) {
      this.menuWidth = menuWidthFromEvent(event)
    },

    toggleDescription (dueDiligenceExemptionId) {
      this.expandedDescriptionId = this.expandedDescriptionId === dueDiligenceExemptionId ? null : dueDiligenceExemptionId
    },

    async enterEdit () {
      if (this.readOnly) return
      if (this.allDueDiligenceExemptions.length === 0) {
        this.loadingList = true
        try {
          await this.fetchDueDiligenceExemptions()
        } finally {
          this.loadingList = false
        }
      }
      this.selectedId = this.exemption?.dueDiligenceExemptionId ?? null
      this.editing = true
    },

    cancelEdit () {
      this.editing = false
      this.selectedId = null
      this.expandedDescriptionId = null
    },

    save () {
      if (!this.canSave) return
      this.$emit('save', { dueDiligenceExemptionId: this.selectedId ?? null })
    }
  }
}
</script>

<style scoped>
.tep-exempt {
  border-color: rgba(var(--color-success-rgb), 0.35) !important;
  background: rgba(var(--color-success-rgb), 0.06);
}
</style>
